import { type JSX } from 'react';
import { formatPostalCode } from '@footsteps/shared';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useWozFindAddressModalViewModel } from './woz-find-address-modal.viewmodel';
import { styles } from './woz-find-address-modal.styles';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (addressData: Record<string, unknown>) => void;
};

export const WozFindAddressModal = ({ open, onClose, onConfirm }: Props): JSX.Element => {
  const [state, viewModel] = useWozFindAddressModalViewModel();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasErrors = Object.keys(state.errors).length > 0 || !!state.apiError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      fullScreen={isMobile}
      slotProps={{ paper: { sx: styles.dialogPaper(isMobile) } }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        Adres opzoeken
        <IconButton aria-label='sluiten' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={styles.dialogContent}>
        <Box
          component='form'
          onSubmit={(e) => {
            e.preventDefault();
            viewModel.handleSearch();
          }}
          sx={styles.form(isMobile)}
        >
          <Box sx={styles.formFields}>
            <Box sx={styles.formField}>
              <Typography variant='caption' className='form-label'>
                Postcode
              </Typography>
              <TextField
                placeholder='1234 AB'
                value={formatPostalCode(state.postalCode)}
                onChange={(e) => viewModel.setPostalCode(e.target.value)}
                error={!!state.errors.postalCode}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 7 } }}
              />
            </Box>
            <Box sx={styles.formField}>
              <Typography variant='caption' className='form-label'>
                Huisnummer
              </Typography>
              <TextField
                placeholder='42'
                value={state.houseNumber}
                onChange={(e) => viewModel.setHouseNumber(e.target.value)}
                error={!!state.errors.houseNumber}
                fullWidth
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
            </Box>
            <Box sx={styles.formField}>
              <Typography variant='caption' className='form-label'>
                Toevoeging
              </Typography>
              <TextField
                placeholder='A'
                value={state.houseNumberAddition}
                onChange={(e) => viewModel.setHouseNumberAddition(e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
          <Button
            type='submit'
            variant='contained'
            size='small'
            disabled={state.isLoading}
            startIcon={
              state.isLoading ? <CircularProgress size={16} color='inherit' /> : <SearchIcon fontSize='small' />
            }
            sx={styles.searchButton(isMobile)}
            fullWidth={isMobile}
          >
            Zoeken
          </Button>
        </Box>

        {hasErrors && (
          <Alert severity='warning' sx={styles.alert}>
            {Object.values(state.errors).map((error, index) => (
              <Typography key={index} variant='body2'>
                {error}
              </Typography>
            ))}
            {state.apiError && <Typography variant='body2'>{state.apiError}</Typography>}
          </Alert>
        )}

        {state.results.length === 1 && state.selectedAddress && (
          <Alert severity='success' icon={<CheckCircleIcon fontSize='small' />} sx={styles.alert}>
            <Typography variant='body2' fontWeight={600}>
              {state.selectedAddress.displayName}
            </Typography>
          </Alert>
        )}

        {state.needsSelection && (
          <Box sx={styles.resultSection}>
            <Typography variant='body2' color='text.secondary' sx={styles.resultLabel}>
              Er zijn meerdere adressen gevonden. Selecteer het juiste adres:
            </Typography>
            <List dense sx={styles.resultList}>
              {state.results.map((address) => (
                <ListItemButton
                  key={address.id}
                  selected={state.selectedAddress?.id === address.id}
                  onClick={() => viewModel.selectAddress(address)}
                >
                  <ListItemText primary={address.displayName} slotProps={{ primary: { variant: 'body2' } }} />
                  {state.selectedAddress?.id === address.id && (
                    <InputAdornment position='end'>
                      <CheckCircleIcon color='primary' fontSize='small' />
                    </InputAdornment>
                  )}
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button size='small' onClick={onClose} sx={styles.textButton}>
          Sluiten
        </Button>
        <Button
          size='small'
          variant='contained'
          onClick={() => viewModel.handleConfirm(onConfirm)}
          disabled={!state.canConfirm}
          sx={styles.confirmButton}
        >
          Bevestigen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
